import React, { useState } from 'react';
import { ArrowLeftIcon, CheckCircleIcon, ShieldCheckIcon } from 'lucide-react';
import type { CartItem } from '../App';
interface CheckoutProps {
  items: CartItem[];
  onNavigate: (page: string) => void;
  onClearCart: () => void;
}
export function Checkout({
  items,
  onNavigate,
  onClearCart
}: CheckoutProps) {
  const [orderType, setOrderType] = useState<'pickup' | 'delivery'>('pickup');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const subtotal = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const tax = subtotal * 0.06;
  const deliveryFee = orderType === 'delivery' ? 5.99 : 0;
  const total = subtotal + tax + deliveryFee;
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitted(true);
    onClearCart();
  };
  if (isSubmitted) {
    return <main className="pt-24 min-h-screen bg-matte px-6 flex items-center justify-center" data-id="element-190">
        <div className="max-w-lg text-center fade-section" data-id="element-191">
          <CheckCircleIcon className="w-20 h-20 text-gold mx-auto mb-8" data-id="element-192" />
          <h1 className="font-serif text-[38px] md:text-[50px] text-white mb-4" data-id="element-193">
            ORDER CONFIRMED
          </h1>
          <div className="w-16 h-px bg-gold mx-auto mb-8" data-id="element-194" />
          <p className="font-sans text-lg text-white/70 mb-4" data-id="element-195">
            Thank you for your order! We're preparing your West African feast.
          </p>
          <p className="font-sans text-base text-white/50 mb-12" data-id="element-196">
            {orderType === 'pickup' ? 'Your order will be ready for pickup in approximately 25-35 minutes.' : 'Your delivery is estimated to arrive in 40-55 minutes.'}
          </p>
          <button onClick={() => {
          onNavigate('home');
          window.scrollTo(0, 0);
        }} className="border border-gold text-gold px-8 py-4 font-sans uppercase tracking-widest text-base font-medium hover:bg-gold/10 transition-all duration-300" data-id="element-197">
            Return Home
          </button>
        </div>
      </main>;
  }
  if (items.length === 0) {
    return <main className="pt-24 min-h-screen bg-matte px-6 flex items-center justify-center" data-id="element-198">
        <div className="text-center" data-id="element-199">
          <h1 className="font-serif text-[38px] text-white mb-4" data-id="element-200">
            No items to checkout
          </h1>
          <button onClick={() => {
          onNavigate('menu');
          window.scrollTo(0, 0);
        }} className="border border-gold text-gold px-8 py-4 font-sans uppercase tracking-widest text-base font-medium hover:bg-gold/10 transition-all duration-300 mt-6" data-id="element-201">
            Browse Menu
          </button>
        </div>
      </main>;
  }
  return <main className="pt-24 min-h-screen bg-matte px-6" data-id="element-202">
      <div className="max-w-6xl mx-auto py-16" data-id="element-203">
        {/* Header */}
        <div className="mb-12 fade-section" data-id="element-204">
          <button onClick={() => {
          onNavigate('cart');
          window.scrollTo(0, 0);
        }} className="flex items-center gap-2 text-white/50 hover:text-gold transition-colors duration-300 font-sans text-sm uppercase tracking-widest mb-4" data-id="element-205">
            <ArrowLeftIcon className="w-4 h-4" data-id="element-206" />
            Back to Cart
          </button>
          <h1 className="font-serif text-[38px] md:text-[50px] text-white" data-id="element-207">
            CHECKOUT
          </h1>
        </div>

        <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-5 gap-12 fade-section" data-id="element-208">
          {/* Left: Form Fields */}
          <div className="lg:col-span-3 space-y-10" data-id="element-209">
            {/* Order Type */}
            <div data-id="element-210">
              <h2 className="font-sans text-sm tracking-ultra-wide text-gold uppercase mb-6" data-id="element-211">
                Order Type
              </h2>
              <div className="grid grid-cols-2 gap-4" data-id="element-212">
                <button type="button" onClick={() => setOrderType('pickup')} className={`py-4 font-sans uppercase tracking-widest text-sm font-medium transition-all duration-300 ${orderType === 'pickup' ? 'bg-gold text-black' : 'border border-white/10 text-white/60 hover:border-gold/30'}`} data-id="element-213">
                  Pickup
                </button>
                <button type="button" onClick={() => setOrderType('delivery')} className={`py-4 font-sans uppercase tracking-widest text-sm font-medium transition-all duration-300 ${orderType === 'delivery' ? 'bg-gold text-black' : 'border border-white/10 text-white/60 hover:border-gold/30'}`} data-id="element-214">
                  Delivery
                </button>
              </div>
            </div>

            {/* Contact Info */}
            <div data-id="element-215">
              <h2 className="font-sans text-sm tracking-ultra-wide text-gold uppercase mb-6" data-id="element-216">
                Contact Information
              </h2>
              <div className="space-y-4" data-id="element-217">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-218">
                  <div data-id="element-219">
                    <label className="block font-sans text-sm text-white/50 mb-2" data-id="element-220">
                      First Name
                    </label>
                    <input type="text" required className="w-full bg-charcoal border border-white/10 px-4 py-3 font-sans text-base text-white placeholder-white/20 focus:border-gold focus:outline-none transition-colors" placeholder="John" data-id="element-221" />
                  </div>
                  <div data-id="element-222">
                    <label className="block font-sans text-sm text-white/50 mb-2" data-id="element-223">
                      Last Name
                    </label>
                    <input type="text" required className="w-full bg-charcoal border border-white/10 px-4 py-3 font-sans text-base text-white placeholder-white/20 focus:border-gold focus:outline-none transition-colors" placeholder="Doe" data-id="element-224" />
                  </div>
                </div>
                <div data-id="element-225">
                  <label className="block font-sans text-sm text-white/50 mb-2" data-id="element-226">
                    Email
                  </label>
                  <input type="email" required className="w-full bg-charcoal border border-white/10 px-4 py-3 font-sans text-base text-white placeholder-white/20 focus:border-gold focus:outline-none transition-colors" placeholder="john@example.com" data-id="element-227" />
                </div>
                <div data-id="element-228">
                  <label className="block font-sans text-sm text-white/50 mb-2" data-id="element-229">
                    Phone
                  </label>
                  <input type="tel" required className="w-full bg-charcoal border border-white/10 px-4 py-3 font-sans text-base text-white placeholder-white/20 focus:border-gold focus:outline-none transition-colors" placeholder="(240) 555-0199" data-id="element-230" />
                </div>
              </div>
            </div>

            {/* Delivery Address (conditional) */}
            {orderType === 'delivery' && <div data-id="element-231">
                <h2 className="font-sans text-sm tracking-ultra-wide text-gold uppercase mb-6" data-id="element-232">
                  Delivery Address
                </h2>
                <div className="space-y-4" data-id="element-233">
                  <div data-id="element-234">
                    <label className="block font-sans text-sm text-white/50 mb-2" data-id="element-235">
                      Street Address
                    </label>
                    <input type="text" required className="w-full bg-charcoal border border-white/10 px-4 py-3 font-sans text-base text-white placeholder-white/20 focus:border-gold focus:outline-none transition-colors" placeholder="123 Main Street" data-id="element-236" />
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4" data-id="element-237">
                    <div className="col-span-2 sm:col-span-1" data-id="element-238">
                      <label className="block font-sans text-sm text-white/50 mb-2" data-id="element-239">
                        City
                      </label>
                      <input type="text" required className="w-full bg-charcoal border border-white/10 px-4 py-3 font-sans text-base text-white placeholder-white/20 focus:border-gold focus:outline-none transition-colors" placeholder="Lanham" data-id="element-240" />
                    </div>
                    <div data-id="element-241">
                      <label className="block font-sans text-sm text-white/50 mb-2" data-id="element-242">
                        State
                      </label>
                      <input type="text" required className="w-full bg-charcoal border border-white/10 px-4 py-3 font-sans text-base text-white placeholder-white/20 focus:border-gold focus:outline-none transition-colors" placeholder="MD" data-id="element-243" />
                    </div>
                    <div data-id="element-244">
                      <label className="block font-sans text-sm text-white/50 mb-2" data-id="element-245">
                        ZIP
                      </label>
                      <input type="text" required className="w-full bg-charcoal border border-white/10 px-4 py-3 font-sans text-base text-white placeholder-white/20 focus:border-gold focus:outline-none transition-colors" placeholder="20706" data-id="element-246" />
                    </div>
                  </div>
                </div>
              </div>}

            {/* Payment */}
            <div data-id="element-247">
              <h2 className="font-sans text-sm tracking-ultra-wide text-gold uppercase mb-6" data-id="element-248">
                Payment
              </h2>
              <div className="space-y-4" data-id="element-249">
                <div data-id="element-250">
                  <label className="block font-sans text-sm text-white/50 mb-2" data-id="element-251">
                    Card Number
                  </label>
                  <input type="text" required className="w-full bg-charcoal border border-white/10 px-4 py-3 font-sans text-base text-white placeholder-white/20 focus:border-gold focus:outline-none transition-colors" placeholder="4242 4242 4242 4242" data-id="element-252" />
                </div>
                <div className="grid grid-cols-2 gap-4" data-id="element-253">
                  <div data-id="element-254">
                    <label className="block font-sans text-sm text-white/50 mb-2" data-id="element-255">
                      Expiry
                    </label>
                    <input type="text" required className="w-full bg-charcoal border border-white/10 px-4 py-3 font-sans text-base text-white placeholder-white/20 focus:border-gold focus:outline-none transition-colors" placeholder="MM / YY" data-id="element-256" />
                  </div>
                  <div data-id="element-257">
                    <label className="block font-sans text-sm text-white/50 mb-2" data-id="element-258">
                      CVC
                    </label>
                    <input type="text" required className="w-full bg-charcoal border border-white/10 px-4 py-3 font-sans text-base text-white placeholder-white/20 focus:border-gold focus:outline-none transition-colors" placeholder="123" data-id="element-259" />
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 mt-4 text-white/30" data-id="element-260">
                <ShieldCheckIcon className="w-4 h-4" data-id="element-261" />
                <span className="font-sans text-sm" data-id="element-262">
                  Secure, encrypted payment
                </span>
              </div>
            </div>

            {/* Special Instructions */}
            <div data-id="element-263">
              <h2 className="font-sans text-sm tracking-ultra-wide text-gold uppercase mb-6" data-id="element-264">
                Special Instructions
              </h2>
              <textarea rows={3} className="w-full bg-charcoal border border-white/10 px-4 py-3 font-sans text-base text-white placeholder-white/20 focus:border-gold focus:outline-none transition-colors resize-none" placeholder="Any allergies or special requests..." data-id="element-265" />
            </div>

            {/* Submit (mobile) */}
            <button type="submit" className="w-full lg:hidden bg-gold text-black py-4 font-sans uppercase tracking-widest text-base font-bold hover:bg-gold-light transition-colors duration-300" data-id="element-266">
              Place Order — ${total.toFixed(2)}
            </button>
          </div>

          {/* Right: Order Summary Sidebar */}
          <div className="lg:col-span-2" data-id="element-267">
            <div className="bg-charcoal border border-white/5 p-8 sticky top-28" data-id="element-268">
              <h3 className="font-sans text-sm tracking-ultra-wide text-gold uppercase mb-6" data-id="element-269">
                Order Summary
              </h3>

              <div className="space-y-4 mb-6 max-h-[300px] overflow-y-auto pr-2" data-id="element-270">
                {items.map(item => <div key={item.id} className="flex items-center gap-4" data-id="element-271">
                    <div className="w-14 h-14 flex-shrink-0 overflow-hidden" data-id="element-272">
                      <img src={item.image} alt={item.name} className="w-full h-full object-cover" data-id="element-273" />
                    </div>
                    <div className="flex-grow min-w-0" data-id="element-274">
                      <p className="font-sans text-sm text-white truncate" data-id="element-275">
                        {item.name}
                      </p>
                      <p className="font-sans text-sm text-white/40" data-id="element-276">
                        Qty: {item.quantity}
                      </p>
                    </div>
                    <span className="font-sans text-sm text-white flex-shrink-0" data-id="element-277">
                      ${(item.price * item.quantity).toFixed(2)}
                    </span>
                  </div>)}
              </div>

              <div className="border-t border-white/10 pt-4 space-y-3" data-id="element-278">
                <div className="flex justify-between font-sans text-base text-white/70" data-id="element-279">
                  <span data-id="element-280">Subtotal</span>
                  <span data-id="element-281">${subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between font-sans text-base text-white/70" data-id="element-282">
                  <span data-id="element-283">Tax (6%)</span>
                  <span data-id="element-284">${tax.toFixed(2)}</span>
                </div>
                {orderType === 'delivery' && <div className="flex justify-between font-sans text-base text-white/70" data-id="element-285">
                    <span data-id="element-286">Delivery Fee</span>
                    <span data-id="element-287">${deliveryFee.toFixed(2)}</span>
                  </div>}
                <div className="w-full h-px bg-white/10 my-2" data-id="element-288" />
                <div className="flex justify-between font-serif text-xl text-white" data-id="element-289">
                  <span data-id="element-290">Total</span>
                  <span className="text-gold" data-id="element-291">${total.toFixed(2)}</span>
                </div>
              </div>

              {/* Submit (desktop) */}
              <button type="submit" className="hidden lg:block w-full bg-gold text-black py-4 font-sans uppercase tracking-widest text-base font-bold hover:bg-gold-light transition-colors duration-300 mt-8" data-id="element-292">
                Place Order
              </button>
            </div>
          </div>
        </form>
      </div>
    </main>;
}